import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { useLang } from "../context/LangContext";

const IMAGES = [
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/zhcnk1u2_IMG-20260923-WA0068.jpg",
    alt: "شدات سقفية SLABDECK في أحد مواقع فكرة بغداد",
    cls: "col-span-7 aspect-[4/3]",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/4xvfuntk_IMG-20260923-WA0067.jpg",
    alt: "أعمال المساحة الليلية بجهاز المحطة الشاملة",
    cls: "col-span-5 aspect-[3/4] mt-12",
  },
];

const BANNER_IMG =
  "https://customer-assets-jai6qajn.emergentagent.net/job_construct-solar-hub/artifacts/klbsvdmt_IMG-20260923-WA0069.jpg";

export default function About() {
  const { t } = useLang();
  return (
    <section id="about" data-testid="about-section" className="relative py-20 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          index={t.about.index}
          eyebrow={t.about.eyebrow}
          title={t.about.title}
          description={t.about.desc}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6 grid grid-cols-12 gap-5 order-2 lg:order-1">
            {IMAGES.map((img, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className={`${img.cls} rounded-lg overflow-hidden clip-frame border border-[#E8DFC5]`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 flex flex-col gap-10">
            {t.about.chapters.map((c, i) => (
              <Reveal key={c.number} delay={i * 0.12}>
                <div
                  data-testid={`manifesto-chapter-${c.number}`}
                  className="group flex gap-6 border-r-2 border-[#E8DFC5] pr-6 transition-colors duration-300 hover:border-emerald-500"
                >
                  <span className="font-accent font-bold text-4xl sm:text-5xl text-stone-300 transition-colors duration-300 group-hover:text-emerald-500 leading-none pt-1">
                    {c.number}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-3">
                      {c.headline}
                    </h3>
                    <p className="text-base leading-relaxed text-stone-500">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <div data-testid="certifications-badges" className="flex flex-wrap gap-3 pt-2">
                {t.about.badges.map((b) => (
                  <span
                    key={b}
                    className="rounded-full border border-emerald-500/30 bg-emerald-500/5 px-4 py-2 text-xs font-semibold text-emerald-700"
                  >
                    {b}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-16 lg:mt-24">
          <div
            data-testid="chairman-message"
            className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center rounded-lg border border-[#E8DFC5] bg-white p-8 lg:p-12 overflow-hidden"
          >
            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
            <div className="lg:col-span-4">
              <div className="relative max-w-[280px] mx-auto">
                <div className="absolute -top-3 -right-3 w-full h-full rounded-lg border border-emerald-500/40" />
                <img
                  src="/images/chairman.jpg"
                  alt={t.about.chairmanRole}
                  loading="lazy"
                  className="relative w-full rounded-lg clip-frame border border-[#E8DFC5] object-cover aspect-[3/4]"
                />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="flex items-center gap-4 mb-6">
                <Quote className="w-8 h-8 text-emerald-500 shrink-0" />
                <span className="font-accent text-sm tracking-widest text-emerald-600">
                  {t.about.chairmanEyebrow}
                </span>
              </div>
              <h3 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-5">
                {t.about.chairmanTitle}
              </h3>
              <p className="text-base leading-loose text-stone-500">
                {t.about.chairmanText}
              </p>
              <div className="mt-7 flex items-center gap-4">
                <span className="h-px w-14 bg-emerald-500/60" />
                <div>
                  <p className="font-heading font-bold text-base text-stone-900">
                    {t.about.chairmanName}
                  </p>
                  <p className="text-sm text-stone-500">
                    {t.about.chairmanRole}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-16 lg:mt-24">
          <div
            data-testid="field-banner"
            className="relative rounded-lg overflow-hidden clip-frame border border-[#E8DFC5] h-[300px] lg:h-[420px]"
          >
            <img
              src={BANNER_IMG}
              alt={t.about.bannerTag}
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#14100A]/90 via-[#14100A]/25 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-7 lg:p-10 flex flex-wrap items-end justify-between gap-4">
              <p className="font-heading font-bold text-2xl sm:text-3xl text-white">
                {t.about.bannerLine1} <span className="text-[#34D399]">{t.about.bannerAccent}</span>
              </p>
              <span className="font-accent text-xs tracking-[0.25em] text-[#CBD5E1]">
                {t.about.bannerTag}
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
